import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, CheckIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { DjaySpecimen, useDjaySelection } from '../../mascot';
import { VARIANT_META, type MascotRarity } from '../../mascot/useVariantUnlocks';
import { RARITY_LABELS } from '../../mascot/rarityLabels';
import { useSkinReveal } from '../../mascot/unlock/useSkinReveal';
import { ConfettiCelebration } from '../../accounts/components/ConfettiCelebration';
import { playSoundIntent } from '../../utils/sound/soundIntent';
import { hapticManager } from '../../utils/hapticManager';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
/**
 * SkinEvolutionReveal — a cinematic, Pokémon-style "evolution" sequence for a
 * newly unlocked Djay look. Plays in three beats:
 *
 *   1. CHARGE  — current (base) Djay pulses, energy gathers, screen darkens.
 *   2. FLASH   — a white-out silhouette swap: the old look bleaches to a
 *                silhouette, the new look bleaches in, then colour returns.
 *   3. REVEAL  — the new look bursts out with rays + confetti, its name +
 *                rarity, and an "Aandoen / Verder" choice.
 *
 * Mounted once globally (AppShell). Any caller — in-session OR the standalone
 * `DJ Goud` long-press easter egg — fires it via `triggerSkinReveal(...)`.
 */
type Phase = 'charge' | 'flash' | 'reveal';
const RARITY_RAYS: Record<MascotRarity, string> = {
  common: '#cbd5e1',
  uncommon: '#34d399',
  rare: '#60a5fa',
  legendary: '#fbbf24',
  secret: '#facc15'
};
/**
 * De woorden komen uit `mascot/rarityLabels.ts`.
 *
 * Hier stond een eigen tabel die op één niveau afweek van de kast ("Bijzonder"
 * versus "Ongewoon"). De uitroeptekens die hier stonden zijn vervallen: de
 * feestelijkheid van dit moment zit in de stralen en de confetti, niet in
 * interpunctie die het niveau een andere naam geeft dan in de verzameling.
 */
const RARITY_LABEL = RARITY_LABELS;
export function SkinEvolutionReveal() {
  const {
    active,
    dismiss
  } = useSkinReveal();
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    setSelectedVariant
  } = useDjaySelection();
  const [phase, setPhase] = useState<Phase>('charge');
  const meta = active ? VARIANT_META[active.variant] : null;
  const rayColor = meta ? RARITY_RAYS[meta.rarity] : RARITY_RAYS.legendary;
  // Drive the cinematic timeline. Reduced-motion users skip straight to the
  // reveal so the experience never traps them in a long animation.
  useEffect(() => {
    if (!active) return;
    setPhase('charge');
    if (!shouldAnimate) {
      setPhase('reveal');
      playSoundIntent('reward.achievement');
      hapticManager.hapticCelebration();
      return;
    }
    playSoundIntent('reward.levelUp');
    hapticManager.hapticHeavy();
    const toFlash = setTimeout(() => {
      setPhase('flash');
      hapticManager.hapticHeavy();
    }, 1400);
    const toReveal = setTimeout(() => {
      setPhase('reveal');
      playSoundIntent('reward.achievement');
      hapticManager.hapticCelebration();
    }, 2300);
    return () => {
      clearTimeout(toFlash);
      clearTimeout(toReveal);
    };
  }, [active, shouldAnimate]);
  const rays = useMemo(() => Array.from({
    length: 12
  }, (_, i) => i / 12 * 360), []);
  if (!active || !meta) return null;
  const fromVariant = active.from ?? 'default';
  const handleEquip = () => {
    setSelectedVariant(active.variant);
    try {
      sessionStorage.setItem('collectionFocus', active.variant);
    } catch {

      /* sessionStorage unavailable — non-critical */}
    dismiss();
  };
  return <AnimatePresence data-id="element-2673" data-mp="2pckq51z6xxg3">
      <div role="dialog" aria-modal="true" aria-label={`Nieuwe look ontgrendeld: ${meta.name}`} className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-id="element-2674" data-mp="2u4uggj47wziw">
        
        {/* Darkening backdrop */}
        <motion.div initial={shouldAnimate ? {
        opacity: 0
      } : false} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-950/85 backdrop-blur-md" onClick={phase === 'reveal' ? dismiss : undefined} data-id="element-2675" data-mp="21o1k6t3m5wr6" />
        

        {/* Rotating ray burst — intensifies through the sequence */}
        <motion.div aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none" animate={shouldAnimate ? {
        rotate: 360,
        opacity: phase === 'reveal' ? 0.9 : 0.4
      } : {
        opacity: 0.6
      }} transition={shouldAnimate ? {
        rotate: {
          duration: 16,
          ease: 'linear',
          repeat: Infinity
        }
      } : undefined} data-id="element-2676" data-mp="h0io97vt7ns7">
          
          <div className="relative w-[140vmin] h-[140vmin]" data-id="element-2677" data-mp="vvyawru28hek">
            {rays.map((deg, i) => <div key={i} className="absolute left-1/2 top-1/2 origin-bottom" style={{
            width: '6vmin',
            height: '70vmin',
            transform: `translate(-50%, -100%) rotate(${deg}deg)`,
            background: `linear-gradient(to top, ${rayColor}00, ${rayColor}55)`,
            opacity: i % 2 === 0 ? 1 : 0.5
          }} data-id="element-2678" data-mp="31on67omq7ejv" data-mpi={i} />)}
          </div>
        </motion.div>

        {/* Confetti only on the final reveal */}
        {phase === 'reveal' && <div className="absolute inset-0 pointer-events-none z-[1]" data-id="element-2679" data-mp="2dss7cofc9w90">
            <ConfettiCelebration data-id="element-2680" data-mp="2irl9jkp6e0tv" />
          </div>}

        {/* Stage */}
        <div className="relative z-10 flex flex-col items-center text-center max-h-[92vh]" data-id="element-2681" data-mp="23v4397vufce6">
          {/* Eyebrow */}
          <motion.p initial={shouldAnimate ? {
          opacity: 0,
          y: -8
        } : false} animate={{
          opacity: 1,
          y: 0
        }} className="mb-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-black uppercase tracking-[0.2em]" data-id="element-2682" data-mp="28uqghhbo8smz">
            
            <SparklesIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2683" data-mp="3p98ezvknj8lb" />
            {active.eyebrow ?? 'Nieuwe look!'}
          </motion.p>

          {/* The evolving avatar */}
          <div className="relative w-56 h-56 flex items-center justify-center" data-id="element-2684" data-mp="3rtoahv9yle9o">
            {/* FROM look — base Djay, present during charge + flash */}
            <motion.div className="absolute inset-0 flex items-center justify-center" animate={shouldAnimate ? {
            opacity: phase === 'reveal' ? 0 : 1,
            scale: phase === 'charge' ? [1, 1.06, 1] : phase === 'flash' ? 0.85 : 1,
            x: phase === 'charge' ? [0, -3, 3, -2, 2, 0] : 0,
            filter: phase === 'flash' ? 'brightness(4) saturate(0)' : 'brightness(1)'
          } : {
            opacity: phase === 'reveal' ? 0 : 1
          }} transition={shouldAnimate ? phase === 'charge' ? {
            duration: 1.1,
            repeat: Infinity,
            ease: 'easeInOut'
          } : {
            duration: 0.45,
            ease: 'easeInOut'
          } : undefined} data-id="element-2685" data-mp="3is6yop3l3yqs">
              
              <DjaySpecimen size="hero" emotion="thinking" look={{
              variant: fromVariant
            }} animate={false} className="w-44 h-44" data-id="element-2686" data-mp="tazhpkp4g6bj" />
              
            </motion.div>

            {/* TO look — the new variant, bleaches in then colourises */}
            <motion.div className="absolute inset-0 flex items-center justify-center" initial={shouldAnimate ? {
            opacity: 0,
            scale: 0.6
          } : false} animate={shouldAnimate ? {
            opacity: phase === 'charge' ? 0 : 1,
            scale: phase === 'reveal' ? 1 : 0.85,
            filter: phase === 'flash' ? 'brightness(4) saturate(0)' : 'brightness(1) saturate(1)'
          } : {
            opacity: phase === 'reveal' ? 1 : 0
          }} transition={shouldAnimate ? phase === 'reveal' ? {
            type: 'spring',
            stiffness: 200,
            damping: 13
          } : {
            duration: 0.4,
            ease: 'easeOut'
          } : undefined} data-id="element-2687" data-mp="1aklc179cu079">
              
              <DjaySpecimen size="hero" emotion="celebrating" look={{
              variant: active.variant
            }} animate={shouldAnimate && phase === 'reveal'} className="w-44 h-44" data-id="element-2688" data-mp="27a2pi8ekwqji" />
              
            </motion.div>

            {/* White flash overlay during the swap */}
            {shouldAnimate && <motion.div aria-hidden="true" className="absolute inset-0 rounded-full bg-white pointer-events-none" initial={{
            opacity: 0,
            scale: 0.4
          }} animate={{
            opacity: phase === 'flash' ? [0, 0.95, 0] : 0,
            scale: phase === 'flash' ? [0.4, 1.4, 1.6] : 0.4
          }} transition={{
            duration: 0.9,
            ease: 'easeInOut'
          }} data-id="element-2689" data-mp="32ixhiquja1up" />}
          </div>

          {/* Reveal copy + actions */}
          <AnimatePresence data-id="element-2690" data-mp="2euo164g3a6ay">
            {phase === 'reveal' && <motion.div key="reveal-copy" initial={shouldAnimate ? {
            opacity: 0,
            y: 16
          } : false} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="mt-6 flex flex-col items-center" data-id="element-2691" data-mp="2estgmr8qcg7e">
              
                <h2 className="text-3xl font-black text-white drop-shadow-lg" data-id="element-2692" data-mp="1mdas6cmer38">
                  {meta.name}
                </h2>
                <span className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider" style={{
              backgroundColor: `${rayColor}22`,
              color: rayColor,
              border: `1px solid ${rayColor}66`
            }} data-id="element-2693" data-mp="3osdww0bv1r3k">
                
                  <SparklesIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2694" data-mp="pd0nd4xhe4w4" />
                  {RARITY_LABEL[meta.rarity]}
                </span>
                <p className="mt-3 max-w-xs text-sm text-white/70" data-id="element-2695" data-mp="bczi7da6obau">
                  Djay is geëvolueerd naar een gloednieuwe look. Wil je 'm
                  meteen aandoen?
                </p>

                <div className="mt-6 flex w-full max-w-xs gap-3" data-id="element-2696" data-mp="2w25fiap5faq1">
                  <Button onClick={dismiss} variant="outline" size="lg" className="flex-1 font-bold bg-white/5 border-white/30 text-white hover:bg-white/10" data-id="element-2697" data-mp="31337tg3vhl59">
                  
                    Later
                  </Button>
                  <Button onClick={handleEquip} size="lg" className="flex-1 font-bold" data-id="element-2698" data-mp="1299b1dg2324x">
                  
                    <CheckIcon className="w-4 h-4 mr-2" aria-hidden="true" data-id="element-2699" data-mp="oi45juq0bfmo" />
                    Aandoen
                  </Button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </AnimatePresence>;
}